import { useCallback, useEffect, useState } from "react";

/**
 * Custom hook to calculate the number of columns for a responsive grid.
 * The gap is the space between cards. Any extra space in the row will be left at the end (right side) of the row,
 * not distributed between cards.
 * The number of columns is calculated to maximize the number of cards that fit in the available width,
 * preferring more columns over wider cards.
 *
 * @param containerRef - ref to the grid container
 * @param minCardWidth - minimum card width in px
 * @param maxCardWidth - maximum card width in px
 * @param gap - space between cards in px
 * @param padding - { left, right } padding in px
 * @returns { columns } - The number of columns to display
 */
export const useGridColumnCount = (
  containerRef: React.RefObject<HTMLElement>,
  minCardWidth: number,
  maxCardWidth: number,
  gap: number,
  padding: { left: number; right: number }
) => {
  const [columns, setColumns] = useState(1);

  const calculate = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const availableWidth = containerWidth - padding.left - padding.right;
    if (availableWidth <= 0) {
      setColumns(1);
      return;
    }
    const maxPossibleCols = Math.floor(
      (availableWidth + gap) / (minCardWidth + gap)
    );
    let bestCols = 1;
    for (let cols = maxPossibleCols; cols >= 1; cols--) {
      const cardWidth = (availableWidth - (cols - 1) * gap) / cols;
      if (cardWidth >= minCardWidth) {
        bestCols = cols;
        break;
      }
    }
    setColumns(bestCols);
  }, [
    containerRef,
    minCardWidth,
    maxCardWidth,
    gap,
    padding.left,
    padding.right,
  ]);

  useEffect(() => {
    calculate();
    if (!containerRef.current) return () => {};
    const resizeObserver = new window.ResizeObserver(calculate);
    resizeObserver.observe(containerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, calculate]);

  return { columns };
};
